'use client';

import { Dialog, Portal } from '@ark-ui/react';
import { MdClose } from 'react-icons/md';
import { Color } from '~/utils/styles/enums';
import {
  CommentsDetailContainer,
  type CommentsDetailContainerProps,
} from '@app/(default-layout)/s/[storyId]/_containers/comments-detail-container';
import { css } from '@styled-system/css';
import { Box, HStack, Paragraph, styled, VStack } from '@styled-system/jsx';

type Props = {
  storyTitle: string;
  isOpen: boolean;
  onOpenChange?: (details: { open: boolean }) => void;
} & CommentsDetailContainerProps;

export const CommentsDetailDialogContainer = ({
  storyTitle,
  isOpen,
  onOpenChange,
  ...props
}: Props) => {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={onOpenChange}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Backdrop />
        <Positioner>
          <Content overflow="hidden">
            <VStack w="100%" h="100%">
              <HStack
                w="100%"
                h="50px"
                px="24px"
                flexShrink="0"
                bg="white"
                justifyContent="space-between"
                alignItems="center"
                boxShadow="0 0 4px rgba(0, 0, 0, 0.15)"
              >
                <Dialog.CloseTrigger>
                  <MdClose
                    color={Color.ACCENT_1000}
                    size={24}
                    aria-label="メニューを閉じる"
                    className={css({
                      cursor: 'pointer',
                      left: '16px',
                    })}
                  />
                </Dialog.CloseTrigger>
                <Paragraph
                  fontWeight="bold"
                  fontSize="lg"
                  textAlign="center"
                  flexGrow={1}
                  animationName="fade-in"
                  animationDuration="0.3s"
                >
                  コメント
                </Paragraph>
                {/* Spacer */}
                <Box w="24px" />
              </HStack>
              <CommentsDetailContainer
                {...props}
                flexGrow={1}
                h="100%"
                overflowY="scroll"
              />
            </VStack>
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(4px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'none',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: 'auto',
    },
    width: {
      base: '100dvw',
      lg: '680px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'left',
    px: {
      base: '0',
    },
    borderRadius: {
      base: '0',
      lg: '8px',
    },
    width: '100%',
    height: '100%',
    _open: {
      animation: 'none',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});
